/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { Alert, Form } from '@ver-uds/uswds-react';
import { Account, AccountType, MouTemplateType } from 'eve-account-client';
import { PageLoader } from '@ver-uds/react';
import { useAuthState } from '@vis-auth/react';
import { useForm } from 'react-hook-form';
import { useEnrollment } from '../../../context/Enrollment/EnrollmentContext';
import {
  useCreateAccountMutation,
  useCreateAccountWithIDMutation,
  useCreateDependentAccountMutation,
  useMouTemplateQuery,
} from '../../../services';
import { getMouTemplateTypeByFormData } from '../utils/AccessMethodUtils';

import SubPageName from '../SubPageName';
import CompanyInformationFormData from '../../../components/Enrollment/CompanyInformation/CompanyInformationFormData';
import { CompanyAddressesFormData } from '../../../components/Enrollment/CompanyAddresses/CompanyAddressesFormData';
import type { AccessMethodFormData } from '../../../components/Enrollment/AccessMethod/AccessMethodFormData';

import CompanyInformation from '../../../components/CompanyInformation';
import EmployerCategory from '../../../components/EmployerCategory';
import CompanyLocations from '../../../components/CompanyLocations';
import CompanyAccess from '../../../components/CompanyAccess/CompanyAccess';
import HiringSitesCount from '../../../components/HiringSitesCount';
import CorporateEVerifyUsersReview from '../../../components/Enrollment/EVerifyUsersReview/CorporateEVerifyUsersReview';
import EmployerEVerifyUsersReview from '../../../components/Enrollment/EVerifyUsersReview/EmployerEVerifyUsersReview';
import ClientEVerifyUsersReview from '../../../components/Enrollment/EVerifyUsersReview/ClientEVerifyUsersReview';
import ChildEVerifyUsersReview from '../../../components/Enrollment/EVerifyUsersReview/ChildEVerifyUsersReview';
import EnrollmentStepperButtons from '../../../components/Enrollment/EnrollmentStepperButtons';

import AuthFileLink from '../../../components/AuthFileLink';
import Section from '../../../components/Section';
import { EnrollmentType, shouldEvePlusToSBeHidden } from '../EnrollmentData';
import { CommonEVerifyUsersReviewProps } from '../../../components/Enrollment/EVerifyUsersReview/BaseEVerifyUsersReview';
import toAccountEnrollment from '../utils/accountEnrollmentMapper';
import TermsAndConditions from '../../../components/Enrollment/TermsAndConditions/TermsAndConditions';
import { scrollToTop } from '../../../utils/util';
import { useToggles } from '../../../context/Toggles/TogglesContext';

const ReviewSubmit = ({ nawToAawToggle }: { nawToAawToggle: string }): JSX.Element => {
  const { enrollmentData, loggedInAccount, setActiveEditPage, setCompletedEnrollment } = useEnrollment();
  const [error, setError] = useState<boolean>(false);
  const { user } = useAuthState();

  const toggles = useToggles();
  const isMfaEnvironment = toggles?.get('MFA_USER_CREATION') === 'on';

  const isCorporateAccountSelected = enrollmentData.accessMethodFormData?.type === AccountType.CORPORATE;

  const hideEvePlusToS = shouldEvePlusToSBeHidden(
    enrollmentData?.accessMethodFormData?.webServiceAccess || loggedInAccount?.webServiceAccess?.enabled,
    enrollmentData.accessMethodFormData?.type || loggedInAccount?.type,
    enrollmentData.employerCategoryFormData?.category || loggedInAccount?.companyInfo?.employerCategory?.category,
  );

  const getEnrollmentType = (): AccountType => {
    if (enrollmentData.enrollmentType === EnrollmentType.CLIENT) {
      return AccountType.CLIENT;
    }
    return (enrollmentData?.accessMethodFormData as AccessMethodFormData)?.type;
  };

  const enrollmentAccount: Pick<Account, 'type' | 'webServiceAccess' | 'companyInfo' | 'selfVerification'> = {
    type: getEnrollmentType(),
    webServiceAccess: {
      enabled: (enrollmentData?.accessMethodFormData as AccessMethodFormData)?.webServiceAccess,
    },
    selfVerification: !isCorporateAccountSelected && enrollmentData.enrollmentType !== EnrollmentType.CLIENT,
    companyInfo: {
      ...(enrollmentData?.companyInformationFormData as CompanyInformationFormData),
      ...(enrollmentData?.companyAddressesFormData as CompanyAddressesFormData),
    },
  };

  const hiringSiteCount = enrollmentData?.hiringSitesFormData?.length;

  const [mutateAccount, { isLoading: isLoadingCreateAccount }] = useCreateAccountMutation();
  const [mutateAccountWithID, { isLoading: isLoadingCreateAccountWithID }] = useCreateAccountWithIDMutation();
  const [mutateDependent, { isLoading: isLoadingCreateDependentAccount }] = useCreateDependentAccountMutation();
  const isLoading = isLoadingCreateAccount || isLoadingCreateAccountWithID || isLoadingCreateDependentAccount;
  const [hasUserReadTermsOfService, setHasUserReadTermsOfService] = useState<boolean>(hideEvePlusToS);
  const [showReadFullTermsOfServiceError, setShowReadFullTermsOfServiceError] = useState<boolean>(hideEvePlusToS);

  type FormValues = {
    iAgreeCheckbox: boolean;
    iAgreeCheckboxEvePlus: boolean;
  };

  const formMethods = useForm<FormValues>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      iAgreeCheckbox: false,
      iAgreeCheckboxEvePlus: false,
    },
  });

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { register, handleSubmit, errors: formErrors } = formMethods;

  const switchToEditPage = (name: SubPageName) => {
    setActiveEditPage(name);
    scrollToTop();
  };

  const onSubmitForm = handleSubmit(async (data: FormValues) => {
    // if user hasnt read the terms of service, show the error message
    if (!hasUserReadTermsOfService) {
      setShowReadFullTermsOfServiceError(true);
      return;
    }
    const accountType = enrollmentData.accessMethodFormData
      ? enrollmentData.accessMethodFormData.type
      : AccountType.CLIENT;
    const webAccess = enrollmentData.accessMethodFormData
      ? enrollmentData.accessMethodFormData.webServiceAccess
      : false;
    const accountEnrollment = toAccountEnrollment(enrollmentData, accountType, webAccess);
    // eslint-disable-next-line camelcase
    const accountId = user?.account_id as string;
    const isDependentEnrollment = [EnrollmentType.CHILD, EnrollmentType.CLIENT].includes(enrollmentData.enrollmentType);
    const mutate = isDependentEnrollment ? mutateDependent : mutateAccountWithID;
    if (isMfaEnvironment && !isDependentEnrollment) {
      await mutateAccount(
        { accountEnrollment },
        {
          onSuccess: (response) => {
            setError(false);
            setCompletedEnrollment(response);
          },
          onError: () => {
            setError(true);
          },
        },
      );
    } else {
      await mutate(
        { accountId, accountEnrollment },
        {
          onSuccess: (response) => {
            setError(false);
            setCompletedEnrollment(response);
          },
          onError: () => {
            setError(true);
          },
        },
      );
    }
  });

  const getMouTemplateType = (): MouTemplateType | undefined => {
    if (enrollmentData.enrollmentType === EnrollmentType.CLIENT) {
      if (loggedInAccount.webServiceAccess.enabled) {
        return MouTemplateType.CLIENTWEBSERVICE;
      }
      return MouTemplateType.CLIENT;
    }
    return getMouTemplateTypeByFormData(enrollmentData.accessMethodFormData);
  };

  const mouTemplateType = getMouTemplateType();

  const getEVerifyUersReview = (): JSX.Element => {
    const props: CommonEVerifyUsersReviewProps = {
      users: enrollmentData?.everifyUserFormData,
      onEditButtonClick: () => switchToEditPage(SubPageName.EVERIFY_USERS),
    };

    switch (enrollmentData.enrollmentType) {
      case EnrollmentType.INITIAL: {
        if (isCorporateAccountSelected) {
          return <CorporateEVerifyUsersReview {...props} />;
        }
        return <EmployerEVerifyUsersReview {...props} />;
      }
      case EnrollmentType.CLIENT:
        return <ClientEVerifyUsersReview {...props} />;
      case EnrollmentType.CHILD:
        return <ChildEVerifyUsersReview {...props} />;
      default:
        return <EmployerEVerifyUsersReview {...props} />;
    }
  };
  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      pageTitle: 'E-Verify - Review Submit',
    });
  }, []);
  return (
    <Form className="review-enrollment maxw-none margin-y-4" onSubmit={onSubmitForm} noValidate>
      <h1 className="margin-bottom-4">Review and Submit Enrollment</h1>
      {isLoading ? <PageLoader id="enrollment-page-loader" label="Loading" /> : <></>}
      <CompanyInformation
        isCorporateAdmin={isCorporateAccountSelected}
        companyInfoData={enrollmentData?.companyInformationFormData}
        onEditButtonClick={() => {
          switchToEditPage(SubPageName.COMPANY_INFORMATION);
        }}
      />
      <hr />
      {!isCorporateAccountSelected && (
        <EmployerCategory
          employerCategory={enrollmentData?.employerCategoryFormData}
          onEditButtonClick={() => {
            switchToEditPage(SubPageName.EMPLOYER_CATEGORY);
          }}
        />
      )}
      <hr />
      <div className="grid-row grid-gap">
        <CompanyLocations
          accountData={enrollmentAccount}
          onEditButtonClick={() => {
            switchToEditPage(SubPageName.COMPANY_ADDRESSES);
          }}
        />
        <hr className="tablet:display-none grid-col-12 margin-x-1" />
        {!isCorporateAccountSelected && (
          <Section title="Hiring Sites" className="grid-col-12 tablet:grid-col-6" data-testid="hiring-sites">
            <HiringSitesCount
              totalCount={hiringSiteCount}
              onEditButtonClick={() => {
                switchToEditPage(SubPageName.HIRING_SITES);
              }}
            />
          </Section>
        )}
      </div>
      <hr />
      {getEVerifyUersReview()}
      <hr />
      <CompanyAccess
        accountData={enrollmentAccount}
        linkToMou={
          mouTemplateType ? (
            <AuthFileLink
              linkText="View MOU"
              query={useMouTemplateQuery}
              queryArg={mouTemplateType}
              aria-label="View MOU in a new tab"
            />
          ) : undefined
        }
      />
      <hr />

      <TermsAndConditions
        register={register}
        setHasUserReadTermsOfService={setHasUserReadTermsOfService}
        isCorporateAccountSelected={isCorporateAccountSelected}
        hideEvePlusToS={hideEvePlusToS}
        linkToMou={
          mouTemplateType ? (
            <AuthFileLink
              linkText="Memorandum of Understanding (MOU)"
              query={useMouTemplateQuery}
              queryArg={mouTemplateType}
              aria-label="View Memorandum of Understanding (MOU) in a new tab"
            />
          ) : undefined
        }
        nawToAawToggle={nawToAawToggle}
      />

      <hr className="margin-top-2 margin-bottom-4" />
      {error && (
        <Alert status="error" slim className="margin-y-3">
          <Alert.Text>
            Your enrollment is unable to be processed at this time. Please wait a few minutes and try again.
          </Alert.Text>
        </Alert>
      )}
      {(formErrors.iAgreeCheckbox || formErrors.iAgreeCheckboxEvePlus) && (
        <Alert status="error" slim className="margin-y-3">
          <Alert.Text>
            You must agree to Terms of Use {!hideEvePlusToS && <>and Terms of Service policies</>} by checking the
            required checkbox{!hideEvePlusToS && <>es</>} above to proceed.
          </Alert.Text>
        </Alert>
      )}
      {!hasUserReadTermsOfService && showReadFullTermsOfServiceError && (
        <Alert status="error" slim className="margin-y-3">
          <Alert.Text>
            You must scroll down to review all conditions of the Terms of Service before you can submit.
          </Alert.Text>
        </Alert>
      )}
      <EnrollmentStepperButtons nextButtonType="submit" noDivider />
    </Form>
  );
};

export default ReviewSubmit;
